import {
  notifyError,
  useNotifications,
  notifySuccess,
} from 'Logic/Notifications.js'
import { useMutation } from 'Data/Api'
import { useDataValue } from 'Simple/Data'

import addSubscribersMutation from './mutation-add-subscribers.graphql.js'
import removeSubscribersMutation from './mutation-remove-subscribers.graphql.js'
import upsertSubscriberStagesMutation from './mutation-upsert-subscriber-stages.graphql.js'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let [, notify] = useNotifications()

  let [, executeAddSubscribersMutation] = useMutation(addSubscribersMutation)
  let [, executeRemoveSubscribersMutation] = useMutation(
    removeSubscribersMutation
  )
  let [, executeUpsertSubscriberStagesMutation] = useMutation(
    upsertSubscriberStagesMutation
  )

  let board_id = useDataValue({
    context: 'selected',
    path: 'id',
    viewPath: props.viewPath,
  })

  return async function onSubmit({ value, originalValue, args, change }) {
    switch (args.type) {
      case 'add-subscribers': {
        let objects = value.employee_ids.flatMap(employee_id =>
          value.stage_ids.map(stage_id => ({
            board_id,
            stage_id,
            user_id: employee_id,
          }))
        )

        let mutationResponse = await executeAddSubscribersMutation({
          objects,
        })

        if (mutationResponse.error) {
          notify(notifyError('Cannot add subscribers, try again.'))
          return
        }

        notify(notifySuccess(`Subscribers updated.`))

        // reset
        change(next => {
          next.employee_ids = []
          next.employees = []
          next.search_term = ''
          next.stage_ids = []
        })

        return
      }

      case 'remove-subscriber': {
        let mutationResponse = await executeRemoveSubscribersMutation({
          board_id,
          user_ids: [value.subscriber.id],
        })

        if (mutationResponse.error) {
          notify(
            notifyError(
              `Failed to remove subscriber ${value.subscriber.employee_name.trim()}.`
            )
          )
          return
        }

        notify(
          notifySuccess(
            `Unsubscribed ${value.subscriber.employee_name.trim()}!`
          )
        )

        // reset
        change(next => {
          next.subscriber = {}
        })

        return
      }

      case 'remove-subscribers': {
        let mutationResponse = await executeRemoveSubscribersMutation({
          board_id,
          user_ids: value.bulk_user_ids,
        })

        if (mutationResponse.error) {
          notify(notifyError('Failed to remove subscribers.'))
          return
        }

        notify(notifySuccess(`Removed!`))

        // reset
        change(next => {
          next.bulk_user_ids = []
          next.subscriber = {}
        })

        return
      }

      case 'update-stages': {
        let { stages, subscriber } = args

        if (stages.length === 0) {
          notify(
            notifyError('Subscriber must be assigned to at least one stage.')
          )
          return
        }

        let current_stages = subscriber.stages.map(stage => stage.id)

        // delete
        let deletes = current_stages.filter(id => !stages.includes(id))

        // insert
        let inserts = stages.filter(s => !current_stages.includes(s))

        let mutationResponse = await executeUpsertSubscriberStagesMutation({
          delete_stage_ids: deletes,
          user_id: subscriber.id,
          insert_objects: inserts.map(stage_id => ({
            board_id,
            stage_id,
            user_id: subscriber.id,
          })),
        })
        if (mutationResponse.error) {
          notify(
            notifyError(
              `Failed to update subscriber ${subscriber.employee_name.trim()} stages.`
            )
          )
          return
        } else {
          notify(notifySuccess(`Updated ${subscriber.employee_name.trim()}!`))
        }

        return
      }

      default: {
        return
      }
    }
  }
}
